import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from 'Layout';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { message, Select } from 'antd';
import { Clock, LayoutGrid } from 'lucide-react';
import {
  QuizDashboard,
  QuizDesktopNav,
  QuizGateScreen,
  QuizLoading,
  QuizMobileNav,
  QuizQuestionCard,
  QuizSaveConfirmModal,
  QuizSidebar,
  QuizSubmitModal,
} from './component';
import { quizService, getCurrentUserId } from './services/quizService';
import {
  assessmentApi,
  mapApiDayStatusToDayStatus,
  mapApiQuestionsToQuestions,
  mapApiResultQuestionsToQuestions,
  parseAnsweredToOptionMap,
  parseResultAnswerMap,
} from './services/assessmentApi';
import { ApiDayItem, DayStatus, OptionKey, Question, SaveIndicatorStatus } from './data/types';
import { DAYS_META, getDayMeta, getQuestionsForDay } from './data/questionsData';
import './quiz.scss';

export const QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ day?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine active day from path params or query params (default to Day 1)
  const initialDay = useMemo(() => {
    const raw = params.day || searchParams.get('day');
    const parsed = Number(raw);
    return parsed >= 1 ? parsed : 1;
  }, [params.day, searchParams]);

  // Determine view mode: 'dashboard' (select day) or 'quiz' (answering questions)
  const isDirectDayAccess = Boolean(params.day || searchParams.get('day'));
  const [viewMode, setViewMode] = useState<'dashboard' | 'quiz'>(
    isDirectDayAccess ? 'quiz' : 'dashboard'
  );

  const [currentDay, setCurrentDay] = useState<number>(initialDay);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<number, OptionKey>>({});
  const [flagged, setFlagged] = useState<number[]>([]);
  const [dayStatus, setDayStatus] = useState<DayStatus>('AVAILABLE');
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [dayStatuses, setDayStatuses] = useState<Record<number, DayStatus>>({
    1: 'AVAILABLE',
    2: 'AVAILABLE',
    3: 'AVAILABLE',
  });

  // API Integration States
  const [apiDays, setApiDays] = useState<ApiDayItem[] | undefined>(undefined);
  const [isDaysLoading, setIsDaysLoading] = useState<boolean>(false);
  const [questionsByDay, setQuestionsByDay] = useState<Record<number, Question[]>>({});
  const [isQuestionsLoading, setIsQuestionsLoading] = useState<boolean>(false);
  const [isSubmittingDay, setIsSubmittingDay] = useState<boolean>(false);
  const [lockedQuestionIds, setLockedQuestionIds] = useState<number[]>([]);

  const [savingStatus, setSavingStatus] = useState<SaveIndicatorStatus>('AUTO');
  const [isSavingAnswer, setIsSavingAnswer] = useState<boolean>(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingJumpIndex, setPendingJumpIndex] = useState<number | null>(null);
  const [isSavePromptOpen, setIsSavePromptOpen] = useState<boolean>(false);

  // Stable refs to prevent cyclical dependency re-renders
  const dayStatusesRef = React.useRef(dayStatuses);
  dayStatusesRef.current = dayStatuses;

  const apiDaysRef = React.useRef(apiDays);
  apiDaysRef.current = apiDays;

  const isFetchingDayRef = React.useRef<number | null>(null);



  // User ID tracking to handle account switching
  const lastUserIdRef = React.useRef<string>(getCurrentUserId());

  // Load cached locked questions for day scoped by user
  useEffect(() => {
    try {
      const uid = getCurrentUserId();
      const raw = localStorage.getItem(`fc_quiz_locked_u_${uid}_day_${currentDay}`);
      if (raw) {
        const ids = JSON.parse(raw);
        if (Array.isArray(ids)) {
          setLockedQuestionIds(ids);
        }
      } else {
        setLockedQuestionIds([]);
      }
    } catch { }
  }, [currentDay]);

  // Student name from user storage
  const studentName = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        // Same order as the header: Vietnamese names put the last name first
        const viName = [u?.last_name, u?.first_name].filter(Boolean).join(' ').trim();
        return viName || u?.full_name_display || u?.fullname || u?.name || u?.username || 'Học viên';
      }
    } catch { }
    return 'Học viên';
  }, []);

  // Helper to sync local state from quizService
  const loadDaySession = useCallback((day: number) => {
    quizService.syncUser();
    const sess = quizService.getDaySession(day);
    setCurrentIndex(sess.currentIndex || 0);

    // Set answers of this day and user
    setAnswers(sess.answers || {});

    // Set locked questions of current user for this day
    try {
      const uid = getCurrentUserId();
      const raw = localStorage.getItem(`fc_quiz_locked_u_${uid}_day_${day}`);
      if (raw) {
        const ids = JSON.parse(raw);
        if (Array.isArray(ids)) {
          setLockedQuestionIds(ids);
        } else {
          setLockedQuestionIds([]);
        }
      } else {
        setLockedQuestionIds([]);
      }
    } catch {
      setLockedQuestionIds([]);
    }

    setFlagged([...(sess.flagged || [])]);

    // Check if day is marked as completed in dayStatuses or apiDays
    const isCompleted =
      dayStatusesRef.current[day] === 'COMPLETED' ||
      apiDaysRef.current
        ?.find((d) => Number(d.day_number || d.id) === Number(day))
        ?.status?.toLowerCase()
        .includes('complete');

    if (isCompleted) {
      setDayStatus('COMPLETED');
    } else if (sess.status) {
      setDayStatus(sess.status);
    } else {
      setDayStatus('AVAILABLE');
    }

    if (sess.submittedAt) setSubmittedAt(sess.submittedAt);
    if (sess.lastSavedAt) setLastSavedAt(sess.lastSavedAt);
  }, []);

  // Fetch list of days from API
  const fetchAssessmentDays = useCallback(async () => {
    setIsDaysLoading(true);
    try {
      const rawData = await assessmentApi.getAssessmentDays();
      const data: ApiDayItem[] = Array.isArray(rawData)
        ? rawData
        : (rawData as any)?.data || (rawData as any)?.days || [];
      if (Array.isArray(data) && data.length > 0) {
        setApiDays(data);
        const statuses: Record<number, DayStatus> = {};
        data.forEach((d) => {
          const dNum = Number(d.day_number || d.id);
          statuses[dNum] = mapApiDayStatusToDayStatus(d.status);
        });
        setDayStatuses((prev) => ({ ...prev, ...statuses }));
      }
    } catch (err) {
      console.warn('Could not fetch assessment days from API, using cached data', err);
    } finally {
      setIsDaysLoading(false);
    }
  }, []);

  // Fetch questions and previously answered results of a day from API
  const fetchDayQuestions = useCallback(async (day: number) => {
    if (isFetchingDayRef.current === day) {
      return;
    }
    isFetchingDayRef.current = day;
    setIsQuestionsLoading(true);

    try {
      const isCompletedDay =
        dayStatusesRef.current[day] === 'COMPLETED' ||
        apiDaysRef.current
          ?.find((d) => Number(d.day_number || d.id) === Number(day))
          ?.status?.toLowerCase()
          .includes('complete');


      // 1. Nếu ngày đã hoàn thành, tải trực tiếp từ /assessment/days/{day}/result
      if (isCompletedDay) {
        try {
          const resultRes = await assessmentApi.getDayResult(day);
          if (resultRes && resultRes.questions && resultRes.questions.length > 0) {
            const mapped = mapApiResultQuestionsToQuestions(resultRes.questions, day);
            const resultAnswers = parseResultAnswerMap(resultRes.questions, mapped);
            // Chỉ coi các câu hỏi thực sự có đáp án là đã trả lời
            const answeredIds = Object.keys(resultAnswers).map(Number);

            setQuestionsByDay((prev) => ({ ...prev, [day]: mapped }));
            // API là nguồn dữ liệu chuẩn: xóa toàn bộ đáp án cũ của ngày này để không giữ lại câu chưa trả lời từ local
            setAnswers((prev) => {
              const dayQIds = new Set(mapped.map((q) => q.id));
              const next = { ...prev };
              dayQIds.forEach((id) => delete next[id]);
              Object.assign(next, resultAnswers);
              return next;
            });
            setLockedQuestionIds(answeredIds);
            try {
              const uid = getCurrentUserId();
              localStorage.setItem(
                `fc_quiz_locked_u_${uid}_day_${day}`,
                JSON.stringify(answeredIds)
              );
            } catch {}
            setDayStatus('COMPLETED');
            setDayStatuses((prev) => ({ ...prev, [day]: 'COMPLETED' }));

            if (resultRes.submitted_at) {
              setSubmittedAt(resultRes.submitted_at);
            }
            quizService.syncDayData(day, {
              status: 'COMPLETED',
              answers: resultAnswers,
              submittedAt: resultRes.submitted_at,
              replaceAnswers: true,
            });
            setIsQuestionsLoading(false);
            return;
          }
        } catch (err) {
          console.warn(`Could not load results for completed Day ${day} from /result API`, err);
        }
      }

      // 2. Tải câu hỏi từ /assessment/days/{day}/questions
      let loadedFromQuestionsApi = false;
      try {
        const res = await assessmentApi.getDayQuestions(day);
        if (res && res.questions && res.questions.length > 0) {
          loadedFromQuestionsApi = true;
          const mappedQuestions = mapApiQuestionsToQuestions(res.questions);
          setQuestionsByDay((prev) => ({ ...prev, [day]: mappedQuestions }));

          // Map already answered questions from backend API
          const remoteAnswers = parseAnsweredToOptionMap(res.answered, mappedQuestions);
          const preLockedIds = Object.keys(remoteAnswers).map(Number);

          // API is the single source of truth for this day's answers!
          setAnswers((prev) => {
            const dayQIds = new Set(mappedQuestions.map((q) => q.id));
            const next = { ...prev };
            // Clear all questions belonging to this day first
            dayQIds.forEach((id) => delete next[id]);
            // Apply only answers returned by backend API for this user
            Object.assign(next, remoteAnswers);
            return next;
          });

          // Locked questions are the ones already answered on backend for this day
          setLockedQuestionIds(preLockedIds);
          try {
            const uid = getCurrentUserId();
            localStorage.setItem(
              `fc_quiz_locked_u_${uid}_day_${day}`,
              JSON.stringify(preLockedIds)
            );
          } catch {}

          // Synchronize quizService local storage session with backend answers
          quizService.syncDayData(day, {
            status: res.status ? mapApiDayStatusToDayStatus(res.status) : 'AVAILABLE',
            answers: remoteAnswers,
            replaceAnswers: true,
          });

          const mappedStatus = res.status ? mapApiDayStatusToDayStatus(res.status) : null;
          if (mappedStatus) {
            setDayStatus(mappedStatus);
            setDayStatuses((prev) => ({ ...prev, [day]: mappedStatus }));
          }

          // 3. CHỈ gọi /result nếu ngày thực sự đã hoàn thành
          if (
            mappedStatus === 'COMPLETED' ||
            res.status === 'completed'
          ) {
            try {
              const resultRes = await assessmentApi.getDayResult(day);
              if (resultRes && resultRes.questions && resultRes.questions.length > 0) {
                const mappedResult = mapApiResultQuestionsToQuestions(resultRes.questions, day);
                const resultAnswers = parseResultAnswerMap(resultRes.questions, mappedResult);
                const answeredIds = Object.keys(resultAnswers).map(Number);

                // Ưu tiên danh sách câu hỏi và đáp án từ kết quả hoàn thành
                setQuestionsByDay((prev) => ({ ...prev, [day]: mappedResult }));
                setAnswers((prev) => {
                  const dayQIds = new Set(mappedResult.map((q) => q.id));
                  const next = { ...prev };
                  dayQIds.forEach((id) => delete next[id]);
                  Object.assign(next, resultAnswers);
                  return next;
                });
                setLockedQuestionIds(answeredIds);
                try {
                  const uid = getCurrentUserId();
                  localStorage.setItem(
                    `fc_quiz_locked_u_${uid}_day_${day}`,
                    JSON.stringify(answeredIds)
                  );
                } catch {}
                setDayStatus('COMPLETED');
                setDayStatuses((prev) => ({ ...prev, [day]: 'COMPLETED' }));

                if (resultRes.submitted_at) {
                  setSubmittedAt(resultRes.submitted_at);
                }
                quizService.syncDayData(day, {
                  status: 'COMPLETED',
                  answers: resultAnswers,
                  submittedAt: resultRes.submitted_at,
                  replaceAnswers: true,
                });
              }
            } catch {
              // Chưa có kết quả, tiếp tục dùng kết quả từ questions API
            }
          }
        }
      } catch (err) {
        console.warn(`Could not load questions for Day ${day} from /questions API`, err);
      }

      // 4. Nếu /questions không thành công, thử tải từ /result
      if (!loadedFromQuestionsApi) {
        try {
          const resultRes = await assessmentApi.getDayResult(day);
          if (resultRes && resultRes.questions && resultRes.questions.length > 0) {
            const mapped = mapApiResultQuestionsToQuestions(resultRes.questions, day);
            const resultAnswers = parseResultAnswerMap(resultRes.questions, mapped);
            const answeredIds = Object.keys(resultAnswers).map(Number);

            setQuestionsByDay((prev) => ({ ...prev, [day]: mapped }));
            setAnswers((prev) => {
              const dayQIds = new Set(mapped.map((q) => q.id));
              const next = { ...prev };
              dayQIds.forEach((id) => delete next[id]);
              Object.assign(next, resultAnswers);
              return next;
            });
            setLockedQuestionIds(answeredIds);
            try {
              const uid = getCurrentUserId();
              localStorage.setItem(
                `fc_quiz_locked_u_${uid}_day_${day}`,
                JSON.stringify(answeredIds)
              );
            } catch {}
            setDayStatus('COMPLETED');
            setDayStatuses((prev) => ({ ...prev, [day]: 'COMPLETED' }));

            if (resultRes.submitted_at) {
              setSubmittedAt(resultRes.submitted_at);
            }
            quizService.syncDayData(day, {
              status: 'COMPLETED',
              answers: resultAnswers,
              submittedAt: resultRes.submitted_at,
              replaceAnswers: true,
            });
          }
        } catch (resultErr) {
          console.warn(`Could not load results for Day ${day} from /result API`, resultErr);
        }
      }
    } finally {
      setIsQuestionsLoading(false);
      isFetchingDayRef.current = null;
    }
  }, []);

  // Initial load for assessment days
  useEffect(() => {
    fetchAssessmentDays();
  }, [fetchAssessmentDays]);

  // Load questions when entering a day session
  useEffect(() => {
    if (viewMode === 'quiz') {
      fetchDayQuestions(currentDay);
    }
  }, [viewMode, currentDay, fetchDayQuestions]);

  // Listen to service external changes without triggering loop
  useEffect(() => {
    const unsubscribe = quizService.subscribe(() => {
      const localStatuses = quizService.getAllDayStatuses();
      setDayStatuses((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.entries(localStatuses).forEach(([dKey, st]) => {
          const dNum = Number(dKey);
          if (next[dNum] !== 'COMPLETED' && next[dNum] !== st) {
            next[dNum] = st;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    });
    return unsubscribe;
  }, []);

  // Detect when user changes (e.g. login with another account)
  useEffect(() => {
    const activeUid = getCurrentUserId();
    if (lastUserIdRef.current !== activeUid) {
      lastUserIdRef.current = activeUid;
      quizService.syncUser();
      setAnswers({});
      setLockedQuestionIds([]);
      setFlagged([]);
      setDayStatuses({});
      setQuestionsByDay({});
      loadDaySession(currentDay);
      fetchAssessmentDays();
      if (viewMode === 'quiz') {
        fetchDayQuestions(currentDay);
      }
    }
  });

  // Sync state when URL params or location changes
  useEffect(() => {
    const raw = params.day || searchParams.get('day');
    if (raw) {
      const parsed = Number(raw);
      const targetDay = parsed >= 1 ? parsed : 1;
      setCurrentDay(targetDay);
      loadDaySession(targetDay);
      setViewMode('quiz');
    } else if (
      location.pathname.endsWith('/dashboard') ||
      location.pathname.endsWith('/foundation') ||
      location.pathname === '/academy/quiz'
    ) {
      setViewMode('dashboard');
      fetchAssessmentDays();
    }
  }, [params.day, searchParams, location.pathname, loadDaySession, fetchAssessmentDays]);

  const questions = useMemo(() => {
    return questionsByDay[currentDay] || getQuestionsForDay(currentDay);
  }, [questionsByDay, currentDay]);

  const meta = useMemo(() => {
    const apiItem = apiDays?.find((d) => Number(d.day_number || d.id) === currentDay);
    const fallback = getDayMeta(currentDay);
    if (apiItem) {
      return {
        day: currentDay,
        title: apiItem.title.replace(/^Ngày\s*\d+\s*[-–—:]\s*/i, '').trim() || apiItem.title,
        group: apiItem.title,
        totalQuestions: apiItem.total_questions || fallback?.totalQuestions || 34,
        count: apiItem.total_questions || fallback?.count || 34,
        status: mapApiDayStatusToDayStatus(apiItem.status),
        progress: apiItem.progress,
      };
    }
    return (
      fallback || {
        day: currentDay,
        title: `Ngày ${currentDay}`,
        group: `Ngày ${currentDay}`,
        totalQuestions: 34,
        count: 34,
      }
    );
  }, [apiDays, currentDay]);

  const daysList = useMemo(() => {
    if (Array.isArray(apiDays) && apiDays.length > 0) {
      return apiDays.map((d) => {
        const dNum = Number(d.day_number || d.id);
        const m = DAYS_META.find((item) => item.day === dNum);
        return {
          day: dNum,
          title: d.title.replace(/^Ngày\s*\d+\s*[-–—:]\s*/i, '').trim() || m?.title || d.title,
        };
      });
    }
    return DAYS_META.map((m) => ({ day: m.day, title: m.title }));
  }, [apiDays]);

  const totalCount = questions.length;
  const currentQuestion = questions[currentIndex] || questions[0];
  const currentPos = currentIndex + 1;
  const isLocked = dayStatus === 'COMPLETED';

  const answeredCount = useMemo(() => {
    const currentQuestionIds = new Set(questions.map((q) => q.id));
    return Object.keys(answers).filter((id) => currentQuestionIds.has(Number(id))).length;
  }, [answers, questions]);

  const remainingCount = Math.max(0, totalCount - answeredCount);
  const flaggedCount = flagged.length;
  const progressPercent = totalCount > 0 ? (answeredCount / totalCount) * 100 : 0;

  // List of unanswered questions for jump list in modal
  const unansweredList = useMemo(() => {
    return questions
      .map((q, idx) => ({ questionId: q.id, pos: idx + 1, index: idx }))
      .filter((item) => !answers[item.questionId]);
  }, [questions, answers]);

  // Calculate total earned score and max score from questions
  const { earnedScore, maxScore } = useMemo(() => {
    let earned = 0;
    let max = 0;
    let hasEarned = false;

    questions.forEach((q) => {
      if (q.earnedScore !== undefined && q.earnedScore !== null) {
        earned += Number(q.earnedScore);
        hasEarned = true;
      }
      if (q.maxScore !== undefined && q.maxScore !== null) {
        max += Number(q.maxScore);
      } else {
        max += 5;
      }
    });

    if (hasEarned || dayStatus === 'COMPLETED') {
      return { earnedScore: earned, maxScore: max };
    }
    return { earnedScore: undefined, maxScore: undefined };
  }, [questions, dayStatus]);

  // Handle switching to a specific day
  const handleSelectDay = useCallback(
    (day: number) => {
      if (day < 1) return;
      setCurrentDay(day);
      setCurrentIndex(0);
      loadDaySession(day);
      setViewMode('quiz');
      navigate(`/academy/quiz/day/${day}`);
      fetchDayQuestions(day);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [navigate, loadDaySession, fetchDayQuestions]
  );

  // Return back to dashboard (day selection)
  const handleGoToDashboard = useCallback(() => {
    setViewMode('dashboard');
    navigate('/academy/quiz/dashboard');
    fetchAssessmentDays();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate, fetchAssessmentDays]);

  // Start current day from gate screen
  const handleStartDay = () => {
    try {
      quizService.startDay(currentDay);
      setDayStatus('IN_PROGRESS');
      loadDaySession(currentDay);
      message.success(`Đã bắt đầu Ngày ${currentDay} — ${meta?.title}`);
    } catch (err: any) {
      message.error(err.message || 'Không thể bắt đầu ngày này.');
    }
  };

  // Submit answer and lock a specific question when advancing
  const submitAndLockQuestion = useCallback(
    async (q: Question) => {
      if (!q) return;
      const qId = q.id;

      // If already locked or no answer selected, nothing to submit
      if (lockedQuestionIds.includes(qId)) return;
      const selectedKey = answers[qId];
      if (!selectedKey) return;

      const selectedOpt = q.options.find((o) => o.key === selectedKey);

      // Lock question immediately
      setLockedQuestionIds((prev) => {
        const next = Array.from(new Set([...prev, qId]));
        try {
          const uid = getCurrentUserId();
          localStorage.setItem(`fc_quiz_locked_u_${uid}_day_${currentDay}`, JSON.stringify(next));
        } catch { }
        return next;
      });

      // Submit answer to API if answerId exists
      if (selectedOpt?.id) {
        setSavingStatus('SAVING');
        try {
          await assessmentApi.submitAnswer(currentDay, qId, selectedOpt.id);
          setSavingStatus('SAVED');
        } catch (err: any) {
          console.warn('API submit answer error on next', err);
          setSavingStatus('SAVED');
        }
      }
    },
    [answers, lockedQuestionIds, currentDay]
  );

  // Directly jump to a question index
  const doDirectJump = useCallback(
    (index: number) => {
      const bounded = Math.max(0, Math.min(totalCount - 1, index));
      setCurrentIndex(bounded);
      quizService.setCurrentIndex(currentDay, bounded);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [totalCount, currentDay]
  );

  // Jump to specific question index from left sidebar (or mobile nav)
  const handleSidebarSelectQuestion = useCallback(
    (targetIndex: number) => {
      if (isSavingAnswer) return;
      const bounded = Math.max(0, Math.min(totalCount - 1, targetIndex));
      if (bounded === currentIndex) return;

      const currentQId = currentQuestion?.id;
      const hasAnswer = currentQId ? Boolean(answers[currentQId]) : false;
      const isAlreadyLocked = currentQId ? lockedQuestionIds.includes(currentQId) : false;

      // Nếu câu hiện tại ĐÃ CHỌN đáp án VÀ CHƯA BỊ KHÓA / CHƯA NỘP -> hiển thị popup hỏi muốn lưu đáp án hay không
      if (hasAnswer && !isAlreadyLocked && !isLocked) {
        setPendingJumpIndex(bounded);
        setIsSavePromptOpen(true);
        return;
      }

      // Ngược lại (chưa chọn đáp án hoặc đã khóa rồi): chuyển câu trực tiếp
      doDirectJump(bounded);
    },
    [isSavingAnswer, totalCount, currentIndex, currentQuestion, answers, lockedQuestionIds, isLocked, doDirectJump]
  );

  // Xử lý khi người dùng chọn "Lưu đáp án" từ popup
  const handleSaveAndJump = useCallback(async () => {
    if (pendingJumpIndex === null) return;
    const target = pendingJumpIndex;
    setIsSavePromptOpen(false);
    setIsSavingAnswer(true);
    try {
      if (currentQuestion) {
        await submitAndLockQuestion(currentQuestion);
      }
    } finally {
      setIsSavingAnswer(false);
      setPendingJumpIndex(null);
      doDirectJump(target);
    }
  }, [pendingJumpIndex, currentQuestion, submitAndLockQuestion, doDirectJump]);

  // Xử lý khi người dùng chọn "Không lưu" từ popup
  const handleDiscardAndJump = useCallback(() => {
    if (pendingJumpIndex === null) return;
    const target = pendingJumpIndex;
    if (currentQuestion) {
      const qId = currentQuestion.id;
      // Bỏ đáp án đã chọn ở câu này
      setAnswers((prev) => {
        const next = { ...prev };
        delete next[qId];
        try {
          quizService.saveAnswers(currentDay, next);
        } catch {}
        return next;
      });
    }
    setPendingJumpIndex(null);
    setIsSavePromptOpen(false);
    doDirectJump(target);
  }, [pendingJumpIndex, currentQuestion, currentDay, doDirectJump]);

  // Đóng popup và ở lại câu hỏi hiện tại
  const handleCloseSavePrompt = useCallback(() => {
    setPendingJumpIndex(null);
    setIsSavePromptOpen(false);
  }, []);

  // Navigation handlers
  const handlePrev = useCallback(() => {
    if (isSavingAnswer) return;
    if (currentIndex > 0) {
      const prevIdx = currentIndex - 1;
      setCurrentIndex(prevIdx);
      quizService.setCurrentIndex(currentDay, prevIdx);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isSavingAnswer, currentIndex, currentDay]);

  const handleNext = useCallback(async () => {
    if (isSavingAnswer) return;

    // Check if current question has an answer that will trigger API submit
    if (currentQuestion) {
      const qId = currentQuestion.id;
      const isAlreadyLocked = lockedQuestionIds.includes(qId);
      const selectedKey = answers[qId];
      const selectedOpt = selectedKey ? currentQuestion.options.find((o) => o.key === selectedKey) : null;
      const willCallApi = !isAlreadyLocked && Boolean(selectedOpt?.id);

      if (willCallApi) {
        setIsSavingAnswer(true);
        try {
          await submitAndLockQuestion(currentQuestion);
        } finally {
          setIsSavingAnswer(false);
        }
      } else {
        await submitAndLockQuestion(currentQuestion);
      }
    }

    if (currentIndex < totalCount - 1) {
      const nextIdx = currentIndex + 1;
      setCurrentIndex(nextIdx);
      quizService.setCurrentIndex(currentDay, nextIdx);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [
    isSavingAnswer,
    currentQuestion,
    lockedQuestionIds,
    answers,
    submitAndLockQuestion,
    currentIndex,
    totalCount,
    currentDay,
  ]);

  // Select or deselect an option (choice only, NOT submitted yet until pressing Next)
  const handleSelectOption = useCallback(
    (key: OptionKey) => {
      // Tắt tính năng sửa/hủy chọn khi xem lại câu hỏi đã trả lời/đã nộp hoặc ngày thi đã hoàn thành
      const isQuestionLocked =
        isLocked || (currentQuestion && lockedQuestionIds.includes(currentQuestion.id));
      if (isQuestionLocked || !currentQuestion) return;

      const qId = currentQuestion.id;
      const isAlreadySelected = answers[qId] === key;

      if (isAlreadySelected) {
        // Hủy chọn đáp án khi chọn lại vào đáp án đã chọn
        setAnswers((prev) => {
          const next = { ...prev };
          delete next[qId];
          return next;
        });

        try {
          quizService.removeAnswer(currentDay, qId);
          const now = new Date().toISOString();
          setLastSavedAt(now);
          setSavingStatus('AUTO');
        } catch (err: any) {
          console.warn('Local remove answer warning', err);
        }
      } else {
        // 1. Immediate UI state update
        setAnswers((prev) => ({
          ...prev,
          [qId]: key,
        }));

        // 2. Persist draft to local storage
        try {
          quizService.saveAnswer(currentDay, qId, key);
          const now = new Date().toISOString();
          setLastSavedAt(now);
          if (dayStatus === 'AVAILABLE') {
            setDayStatus('IN_PROGRESS');
          }
          setSavingStatus('AUTO');
        } catch (err: any) {
          console.warn('Local save warning', err);
        }
      }
    },
    [isLocked, currentQuestion, lockedQuestionIds, answers, currentDay, dayStatus]
  );

  // Toggle flag for current question
  const handleToggleFlag = useCallback(() => {
    if (isLocked || !currentQuestion) return;

    const qId = currentQuestion.id;
    setFlagged((prev) => {
      const exists = prev.includes(qId);
      return exists ? prev.filter((id) => id !== qId) : [...prev, qId];
    });

    try {
      quizService.toggleFlag(currentDay, qId);
    } catch {
      // Ignore flag error
    }
  }, [isLocked, currentQuestion, currentDay]);

  // Submit day completion
  const handleSubmitDay = async () => {
    try {
      setSubmitError(null);
      setIsSubmittingDay(true);

      // If current question has an answer and hasn't been submitted/locked yet, submit it now
      if (currentQuestion && !lockedQuestionIds.includes(currentQuestion.id)) {
        await submitAndLockQuestion(currentQuestion);
      }

      // 1. Submit to API endpoint: POST /assessment/days/{dayId}/submit
      try {
        await assessmentApi.submitDay(currentDay);
      } catch (apiErr: any) {
        console.warn('API submitDay error, updating locally', apiErr);
        const msg = apiErr.response?.data?.message;
        if (msg && !msg.toLowerCase().includes('network')) {
          throw new Error(msg);
        }
      }

      // 2. Local session update
      quizService.submitDay(currentDay);
      setIsSubmitModalOpen(false);
      setDayStatus('COMPLETED');
      setDayStatuses((prev) => ({ ...prev, [currentDay]: 'COMPLETED' }));
      setSubmittedAt(new Date().toISOString());

      // 3. Tải lại kết quả và đáp án từ API result để hiển thị trực tiếp
      message.success(`Đã hoàn thành Ngày ${currentDay}! Đang nạp kết quả...`);
      await fetchDayQuestions(currentDay);
      fetchAssessmentDays();
    } catch (err: any) {
      setSubmitError(err.message || 'Chưa thể hoàn thành ngày.');
    } finally {
      setIsSubmittingDay(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (viewMode !== 'quiz') return;

      // Don't capture when typing in inputs/textareas
      const target = e.target as HTMLElement | null;
      if (target && /INPUT|TEXTAREA|SELECT/.test(target.tagName)) return;

      // Don't capture when modal is open or when saving answer
      if (isSubmitModalOpen || isSavingAnswer) return;

      // Only active during quiz session
      if (dayStatus !== 'IN_PROGRESS' && dayStatus !== 'COMPLETED') return;

      const k = e.key.toUpperCase();
      const isCurrentQuestionLocked =
        isLocked || lockedQuestionIds.includes(currentQuestion?.id || 0);

      // Options selection (A, B, C, D, E)
      if (!isCurrentQuestionLocked && ['A', 'B', 'C', 'D', 'E'].includes(k)) {
        e.preventDefault();
        handleSelectOption(k as OptionKey);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (k === 'F' && !isCurrentQuestionLocked) {
        e.preventDefault();
        handleToggleFlag();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    viewMode,
    isSubmitModalOpen,
    isSavingAnswer,
    dayStatus,
    isLocked,
    lockedQuestionIds,
    currentQuestion,
    handleSelectOption,
    handleNext,
    handlePrev,
    handleToggleFlag,
  ]);


  const formatTime = (iso: string | null) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return '';
    }
  };

  const getSaveLabel = () => {
    if (isLocked) return 'Đã khóa';
    if (savingStatus === 'SAVING') return 'Đang lưu…';
    if (lastSavedAt) return `Đã lưu · ${formatTime(lastSavedAt)}`;
    return 'Tự động lưu';
  };

  // Check if Day is at gate (not yet started)
  const isGateMode = dayStatus === 'AVAILABLE';

  return (
    <Layout>
      <div className="fc-quiz-root">
        <Helmet>
          <title>
            {viewMode === 'dashboard'
              ? 'Đánh giá Năng lực — Chọn ngày & Tổng quan'
              : `Đánh giá Năng lực — Ngày ${currentDay}: ${meta?.title || ''}`}
          </title>
        </Helmet>

        {/* MODE 1: Student Dashboard (Select Day Screen) */}
        {viewMode === 'dashboard' ? (
          <QuizDashboard
            studentName={studentName}
            onSelectDay={handleSelectDay}
            apiDays={apiDays}
            isLoading={isDaysLoading}
          />
        ) : (
          /* MODE 2: Quiz Workspace / Question assessment */
          <>
            {isQuestionsLoading ? (
              <QuizLoading
                type="workspace"
                message={`Đang tải câu hỏi Ngày ${currentDay} — ${meta?.title || ''}...`}
              />
            ) : (
              <>
                {/* In-page subbar (without full-bleed dark navbar) */}
                {!isGateMode && (
                  <>
                    <div className="fc-quiz-subbar">
                      <div className="fc-subbar-title-group">
                        <button
                          type="button"
                          className="fc-subbar-dash-btn"
                          onClick={handleGoToDashboard}
                          title="Quay lại bảng chọn ngày"
                        >
                          <LayoutGrid size={14} />
                          <span>Chọn ngày</span>
                        </button>

                        <span className="fc-subbar-badge">Ngày {currentDay}</span>
                        <span className="fc-subbar-title">{meta?.title || ''}</span>
                      </div>

                      <div className="fc-subbar-controls">
                        <Select
                          className="fc-day-select"
                          value={currentDay}
                          onChange={(val) => handleSelectDay(Number(val))}
                          style={{ width: 110 }}
                          options={daysList.map((m) => {
                            const st = dayStatuses[m.day] || 'AVAILABLE';
                            const isLockedTab = st === 'LOCKED';
                            return {
                              value: m.day,
                              label: `Ngày ${m.day}`,
                              disabled: isLockedTab,
                            };
                          })}
                        />

                        <div className={`fc-save-indicator ${isLocked ? 'locked' : savingStatus.toLowerCase()}`}>
                          <Clock size={13} />
                          <span>{getSaveLabel()}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Main Workspace / Gate Mode */}
                {isGateMode ? (
                  <QuizGateScreen
                    day={currentDay}
                    title={meta?.title || ''}
                    status={dayStatus}
                    totalCount={totalCount}
                    dayStatuses={dayStatuses}
                    daysList={daysList}
                    onStartDay={handleStartDay}
                    onSelectDay={handleSelectDay}
                    onExit={handleGoToDashboard}
                  />
                ) : (
                  <div className="fc-workspace">
                    {/* Desktop Collapsible Sidebar */}
                    <QuizSidebar
                      day={currentDay}
                      totalCount={totalCount}
                      answeredCount={answeredCount}
                      remainingCount={remainingCount}
                      flaggedCount={flaggedCount}
                      earnedScore={earnedScore}
                      maxScore={maxScore}
                      questions={questions}
                      answers={answers}
                      flagged={flagged}
                      currentIndex={currentIndex}
                      isCollapsed={isSidebarCollapsed}
                      canSubmit={!isLocked}
                      lockedQuestionIds={lockedQuestionIds}
                      isDayCompleted={isLocked}
                      isSaving={isSavingAnswer}
                      onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
                      onSelectQuestion={handleSidebarSelectQuestion}
                      onOpenSubmitModal={() => {
                        setSubmitError(null);
                        setIsSubmitModalOpen(true);
                      }}
                    />

                    {/* Main Question & Assessment Area */}
                    <main className="fc-content-area">
                      {/* Mobile Top Strip */}
                      <QuizMobileNav
                        day={currentDay}
                        currentPos={currentPos}
                        totalCount={totalCount}
                        answeredCount={answeredCount}
                        questions={questions}
                        answers={answers}
                        flagged={flagged}
                        currentIndex={currentIndex}
                        isFirst={currentIndex === 0}
                        isLast={currentIndex === totalCount - 1}
                        isLocked={isLocked}
                        isSaving={isSavingAnswer}
                        lockedQuestionIds={lockedQuestionIds}
                        canSubmit={!isLocked}
                        onPrev={handlePrev}
                        onNext={handleNext}
                        onSelectQuestion={handleSidebarSelectQuestion}
                        onOpenSubmitModal={() => {
                          setSubmitError(null);
                          setIsSubmitModalOpen(true);
                        }}
                      />

                      <div className="fc-content-wrapper">
                        {/* Question Card */}
                        {currentQuestion && (
                          <QuizQuestionCard
                            question={currentQuestion}
                            pos={currentPos}
                            total={totalCount}
                            selectedKey={answers[currentQuestion.id]}
                            isFlagged={flagged.includes(currentQuestion.id)}
                            isLocked={isLocked || lockedQuestionIds.includes(currentQuestion.id)}
                            isSaving={isSavingAnswer}
                            isDayCompleted={isLocked}
                            submittedAt={submittedAt}
                            day={currentDay}
                            onSelectOption={handleSelectOption}
                            onToggleFlag={handleToggleFlag}
                          />

                        )}

                        {/* Desktop Bottom Navigation Bar */}
                        <QuizDesktopNav
                          day={currentDay}
                          currentPos={currentPos}
                          totalCount={totalCount}
                          answeredCount={answeredCount}
                          remainingCount={remainingCount}
                          questions={questions}
                          answers={answers}
                          currentIndex={currentIndex}
                          isFirst={currentIndex === 0}
                          isLast={currentIndex === totalCount - 1}
                          isLocked={isLocked}
                          isSaving={isSavingAnswer}
                          onPrev={handlePrev}
                          onNext={handleNext}
                          onSelectQuestion={handleSidebarSelectQuestion}
                          onOpenSubmitModal={() => {
                            setSubmitError(null);
                            setIsSubmitModalOpen(true);
                          }}
                        />
                      </div>
                    </main>
                  </div>
                )}

                {/* Confirmation & Jump Modal */}
                <QuizSubmitModal
                  isOpen={isSubmitModalOpen}
                  day={currentDay}
                  totalCount={totalCount}
                  answeredCount={answeredCount}
                  unansweredList={unansweredList}
                  submitError={submitError}
                  isSubmitting={isSubmittingDay}
                  onClose={() => setIsSubmitModalOpen(false)}
                  onSubmit={handleSubmitDay}
                  onJumpToQuestion={doDirectJump}
                />

                {/* Popup hỏi lưu đáp án khi nhấn chuyển câu ở ô chuyển câu bên trái */}
                <QuizSaveConfirmModal
                  isOpen={isSavePromptOpen}
                  currentPos={currentPos}
                  targetPos={pendingJumpIndex !== null ? pendingJumpIndex + 1 : 1}
                  selectedKey={currentQuestion ? answers[currentQuestion.id] : undefined}
                  isSaving={isSavingAnswer}
                  onSaveAndJump={handleSaveAndJump}
                  onDiscardAndJump={handleDiscardAndJump}
                  onClose={handleCloseSavePrompt}
                />
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default QuizPage;
